import React from 'react';
import {
  AvatarCell,
  AvatarCellComponentProps,
  AvatarSkeleton,
  DropdownButtonCell,
  DropdownButtonCellComponentProps,
  DropdownButtonSkeleton,
  FilterItems,
  MenuItemProps,
  getFaIcon,
  getFixedWidthFaIcon,
} from '@odin-labs/components';
import { ensureNonEmptyItems } from 'utils';
import { AuthUser, to } from 'acl';
import {
  faEdit,
  faTrash,
  faUserMinus,
  faUserPlus,
  faWrench,
} from 'components/icons';
import {
  JobsiteContractor,
  JobsiteContractorColumn,
  JobsiteContractorFilters,
  JobsiteContractorModalValue,
} from './types';

const EditIcon = getFixedWidthFaIcon({ icon: faEdit });
const TrashIcon = getFixedWidthFaIcon({ icon: faTrash });
const UserMinusIcon = getFixedWidthFaIcon({ icon: faUserMinus });
const UserPlusIcon = getFixedWidthFaIcon({ icon: faUserPlus });
const ContractorIcon = getFaIcon({
  icon: faWrench,
  className: 'odin-text-base odin-text-odin-primary',
});

export const getColumns = ({
  user,
  openModal,
}: {
  user: AuthUser;
  openModal: (value: JobsiteContractorModalValue) => void;
}): JobsiteContractorColumn[] => {
  const canEditContractorAssignments = user.isAllowed(
    to.editContractorJobsiteAssignments,
  );
  const canRemoveContractorAssignments = user.isAllowed(
    to.removeContractorJobsiteAssignments,
  );
  const isActionsButtonVisible =
    canEditContractorAssignments || canRemoveContractorAssignments;

  return ensureNonEmptyItems<JobsiteContractorColumn>([
    {
      id: 'contactor',
      Header: 'Contractor',
      accessor: (jobsiteContractor: JobsiteContractor): string => {
        return jobsiteContractor.contractor?.organization?.name;
      },
      Cell: AvatarCell,
      CellLoading: <AvatarSkeleton size="xs" type="rounded" hideDetails />,
      componentProps: (
        jobsiteContractor: JobsiteContractor,
      ): AvatarCellComponentProps => ({
        icon: ContractorIcon,
        type: 'rounded',
        placeholder: (
          jobsiteContractor?.contractor.organization.name.substring(0, 2) ||
          '??'
        ).toUpperCase(),
        objectFit: 'cover',
        size: 'xs',
      }),
      disableSortBy: true,
    },
    {
      id: 'workerCount',
      Header: <>Worker&nbsp;Count</>,
      accessor: (jobsiteContractor: JobsiteContractor): string =>
        jobsiteContractor?.jobsiteWorkers.count.toString() || '',
      disableSortBy: true,
    },
    isActionsButtonVisible && {
      Header: '',
      id: 'actions',
      Cell: DropdownButtonCell,
      CellLoading: (): React.ReactElement => (
        <DropdownButtonSkeleton cellAlignment="right" />
      ),
      componentProps: (
        jobsiteContractor: JobsiteContractor,
      ): DropdownButtonCellComponentProps => ({
        cellAlignment: 'right',
        menuItems: ensureNonEmptyItems<MenuItemProps>([
          user.isAllowed(to.editContractorJobsiteAssignments) && {
            icon: EditIcon,
            text: 'Edit Assignment',
            theme: 'secondary',
            onClick: (): void =>
              openModal({ jobsiteContractor, modalAction: 'edit' }),
          },
          user.isAllowed(to.removeContractorJobsiteAssignments) && {
            icon: TrashIcon,
            text: 'Delete Assignment',
            theme: 'danger',
            onClick: (): void =>
              openModal({ jobsiteContractor, modalAction: 'remove' }),
          },
          user.isAllowed(
            to.offboardContractors,
            jobsiteContractor.jobsite.jobsiteId,
          ) && {
            icon: UserMinusIcon,
            text: 'Offboard All Workers',
            theme: 'secondary',
            onClick: (): void =>
              openModal({ jobsiteContractor, modalAction: 'offboard' }),
          },
          user.isAllowed(
            to.reinstateContractors,
            jobsiteContractor.jobsite.jobsiteId,
          ) && {
            icon: UserPlusIcon,
            text: 'Reinstate All Workers',
            theme: 'secondary',
            onClick: (): void =>
              openModal({ jobsiteContractor, modalAction: 'reinstate' }),
          },
        ]),
      }),
    },
  ]);
};

export const getFilterItems = ({
  search,
  isProcoreEnabled,
  procoreStatus,
}: {
  search: string;
  isProcoreEnabled: boolean;
  procoreStatus?: string;
}): FilterItems<JobsiteContractorFilters> => ({
  ...(isProcoreEnabled
    ? {
        procoreStatus: {
          header: 'Procore Status',
          type: 'dropdown',
          defaultValue: procoreStatus,
          componentProps: {
            options: [
              {
                value: 'linked',
                label: 'Linked',
              },
              {
                value: 'unlinked',
                label: 'Unlinked',
              },
            ],
            allowSearch: false,
            isMulti: false,
          },
        },
      }
    : {}),
  search: {
    header: '',
    type: 'searchInput',
    defaultValue: search,
    isVisibleOutsideFilterOnMobile: true,
  },
});
